// Downloads KSF surf maps from the public Google Drive archive (through the dev / preview server's proxy or the SURF
// relay, src/maps/relay.ts: Drive refuses cross-site downloads from web pages), extracts the BSP from .rar/.zip/.bz2 archives in the browser and caches the BSP in
// IndexedDB so each map is downloaded only once.
import { unzipSync } from 'fflate';
import { bunzip2 } from '../bsp/bz2';
import type { LoadProgress } from '../game/api';
import type { CatalogEntry } from './catalog';
import { DRIVE_PROXY_HEADER, driveDownloadUrl, driveProxyPath } from './drive';
import { getRelayBase, relayUrl } from './relay';

export { driveDownloadUrl } from './drive';

const DB_NAME = 'surf-maps';
const STORE = 'bsp';

/**
 * The same file through the dev / preview server's Drive proxy (vite.config.ts). Drive answers a web page's
 * cross-site download with 403 and no CORS header, so the browser can't fetch the archives itself; the local
 * server (or the relay) can. Proxy answers carry an `x-surf-drive-proxy` header.
 */
export function driveProxyUrl(driveId: string): string {
  return driveProxyPath(driveId);
}

export function driveViewUrl(driveId: string): string {
  return `https://drive.google.com/file/d/${encodeURIComponent(driveId)}/view`;
}

// ---------------------------------------------------------------- IndexedDB cache

interface CachedMap {
  name: string;
  data: ArrayBuffer;
  size: number;
  date: number;
}

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null);
  if (!dbPromise) {
    dbPromise = new Promise((resolve) => {
      try {
        const req = indexedDB.open(DB_NAME, 1);
        req.onupgradeneeded = () => {
          const db = req.result;
          if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'name' });
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve(null);
        req.onblocked = () => resolve(null);
      } catch {
        resolve(null);
      }
    });
  }
  return dbPromise;
}

function idb<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest): Promise<T | null> {
  return openDb().then(
    (db) =>
      new Promise<T | null>((resolve) => {
        if (!db) return resolve(null);
        try {
          const tx = db.transaction(STORE, mode);
          const req = fn(tx.objectStore(STORE));
          req.onsuccess = () => resolve(req.result as T);
          req.onerror = () => resolve(null);
        } catch {
          resolve(null);
        }
      }),
  );
}

async function getCached(name: string): Promise<ArrayBuffer | null> {
  const rec = await idb<CachedMap>('readonly', (s) => s.get(name.toLowerCase()));
  return rec?.data ?? null;
}

async function putCached(name: string, data: ArrayBuffer): Promise<void> {
  try {
    await navigator.storage?.persist?.();
  } catch {
    /* ignore */
  }
  await idb('readwrite', (s) => s.put({ name: name.toLowerCase(), data, size: data.byteLength, date: Date.now() } satisfies CachedMap));
}

export async function listCachedMaps(): Promise<string[]> {
  const keys = await idb<IDBValidKey[]>('readonly', (s) => s.getAllKeys());
  return (keys ?? []).map(String).sort();
}

export async function deleteCachedMap(name: string): Promise<void> {
  await idb('readwrite', (s) => s.delete(name.toLowerCase()));
}

// ---------------------------------------------------------------- download

const MB = 1024 * 1024;
const fmtMB = (n: number) => (n / MB).toFixed(1);

const NO_DIRECT_DOWNLOAD =
  'Google Drive blocks map downloads straight from a web page. Run the game with `npm run dev` or `npm run preview` ' +
  '(the local server downloads the maps for it), build it with a SURF relay (VITE_SURF_RELAY, see the README), ' +
  'or download the map yourself and drop the file on the menu.';

/** Where a Drive download came from. */
export type DriveSource = 'local' | 'relay' | 'direct';

export interface OpenDriveOptions {
  signal?: AbortSignal;
  /** Relay base URL (default: getRelayBase()); null: no relay. */
  relay?: string | null;
  fetch?: (input: string, init?: RequestInit) => Promise<Response>;
}

/**
 * The page's own server's Drive proxy when there is one (dev / preview), else the relay when one is configured, else
 * Drive itself (which a browser normally refuses).
 */
export async function openDrive(driveId: string, opts: OpenDriveOptions = {}): Promise<{ res: Response; source: DriveSource }> {
  const { signal } = opts;
  const fetchFn = opts.fetch ?? ((i: string, init?: RequestInit) => fetch(i, init));
  const relay = opts.relay === undefined ? getRelayBase() : opts.relay;
  try {
    const res = await fetchFn(driveProxyUrl(driveId), { signal });
    if (res.headers.get(DRIVE_PROXY_HEADER)) return { res, source: 'local' };
    if (res.body) void res.body.cancel().catch(() => undefined);
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
  }
  if (relay) {
    let res: Response;
    try {
      res = await fetchFn(relayUrl(relay, driveProxyUrl(driveId)), { signal, mode: 'cors', credentials: 'omit' });
    } catch (e) {
      if ((e as Error).name === 'AbortError') throw e;
      throw new Error(`The map relay (${relay}) could not be reached. Try again later, or download the map yourself and drop the file on the menu.`);
    }
    if (res.headers.get(DRIVE_PROXY_HEADER)) return { res, source: 'relay' };
    if (res.body) void res.body.cancel().catch(() => undefined);
    throw new Error(`${relay} is not a SURF relay (HTTP ${res.status}, no ${DRIVE_PROXY_HEADER} header).`);
  }
  try {
    return { res: await fetchFn(driveDownloadUrl(driveId), { signal, mode: 'cors', credentials: 'omit' }), source: 'direct' };
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new Error(NO_DIRECT_DOWNLOAD);
  }
}

async function download(driveId: string, onProgress?: (p: LoadProgress) => void, signal?: AbortSignal): Promise<ArrayBuffer> {
  const { res, source } = await openDrive(driveId, { signal });
  if ((res.status === 502 || res.status === 504) && source !== 'direct') {
    const who = source === 'relay' ? 'The map relay' : 'The local server';
    throw new Error(`${who} could not reach Google Drive: ${(await res.text()).slice(0, 200)}`);
  }
  if (!res.ok) throw new Error(`Google Drive answered HTTP ${res.status}. The file may be rate-limited; try again later.`);
  const type = res.headers.get('content-type') ?? '';
  if (type.includes('text/html')) {
    throw new Error('Google Drive returned a web page instead of the file (download quota exceeded or file moved). Try again later.');
  }
  const total = Number(res.headers.get('content-length')) || 0;
  if (!res.body) {
    const buf = await res.arrayBuffer();
    onProgress?.({ phase: 'download', message: `Downloading ${fmtMB(buf.byteLength)} MB`, loaded: buf.byteLength, total: buf.byteLength });
    return buf;
  }
  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;
  let lastReport = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    loaded += value.byteLength;
    const now = performance.now();
    if (now - lastReport > 80) {
      lastReport = now;
      onProgress?.({
        phase: 'download',
        message: total ? `Downloading ${fmtMB(loaded)} / ${fmtMB(total)} MB` : `Downloading ${fmtMB(loaded)} MB`,
        loaded,
        total: total || undefined,
      });
    }
  }
  const out = new Uint8Array(loaded);
  let off = 0;
  for (const c of chunks) {
    out.set(c, off);
    off += c.byteLength;
  }
  onProgress?.({ phase: 'download', message: `Downloaded ${fmtMB(loaded)} MB`, loaded, total: loaded });
  return out.buffer;
}

// ---------------------------------------------------------------- extraction

function startsWith(b: Uint8Array, sig: number[]): boolean {
  if (b.length < sig.length) return false;
  for (let i = 0; i < sig.length; i++) if (b[i] !== sig[i]) return false;
  return true;
}

const SIG_VBSP = [0x56, 0x42, 0x53, 0x50];
const SIG_BZ2 = [0x42, 0x5a, 0x68];
const SIG_RAR = [0x52, 0x61, 0x72, 0x21, 0x1a, 0x07];
const SIG_ZIP = [0x50, 0x4b, 0x03, 0x04];
const SIG_7Z = [0x37, 0x7a, 0xbc, 0xaf, 0x27, 0x1c];

function baseName(path: string): string {
  const p = path.replace(/\\/g, '/').split('/').pop() ?? path;
  return p.replace(/\.bz2$/i, '').replace(/\.bsp$/i, '').replace(/\.(rar|zip|7z)$/i, '').toLowerCase();
}

let unrarWasm: Promise<ArrayBuffer | undefined> | null = null;

async function getUnrarWasm(): Promise<ArrayBuffer | undefined> {
  if (typeof window === 'undefined') return undefined; // node: the library loads its own wasm
  if (!unrarWasm) {
    unrarWasm = (async () => {
      const { default: url } = await import('node-unrar-js/esm/js/unrar.wasm?url');
      const res = await fetch(url);
      return res.arrayBuffer();
    })();
  }
  return unrarWasm;
}

function pickBsp<T extends { name: string; size: number }>(files: T[], hint: string): T | undefined {
  const bsps = files.filter((f) => /\.bsp$/i.test(f.name));
  if (!bsps.length) return undefined;
  return bsps.find((f) => baseName(f.name) === hint) ?? bsps.sort((a, b) => b.size - a.size)[0];
}

/** Returns the BSP inside a downloaded/dropped file (.bsp, .bsp.bz2, .rar, .zip). */
export async function extractMapArchive(data: ArrayBuffer, fileName: string): Promise<{ name: string; bsp: ArrayBuffer }> {
  const bytes = new Uint8Array(data);
  const hint = baseName(fileName);
  if (startsWith(bytes, SIG_VBSP)) return { name: hint, bsp: data };
  if (startsWith(bytes, SIG_BZ2)) {
    const out = bunzip2(bytes);
    if (!startsWith(out, SIG_VBSP)) throw new Error('The .bz2 file does not contain a BSP map.');
    // bunzip2 returns an exact-size buffer at offset 0: no copy needed.
    const exact = out.byteOffset === 0 && out.byteLength === out.buffer.byteLength;
    return { name: hint, bsp: (exact ? out.buffer : out.slice().buffer) as ArrayBuffer };
  }
  if (startsWith(bytes, SIG_RAR)) {
    const { createExtractorFromData } = await import('node-unrar-js');
    const wasmBinary = await getUnrarWasm();
    const extractor = await createExtractorFromData({ data, wasmBinary });
    const headers = [...extractor.getFileList().fileHeaders].map((h) => ({ name: h.name, size: h.unpSize }));
    const target = pickBsp(headers, hint);
    if (!target) throw new Error(`No .bsp file inside ${fileName}.`);
    const extracted = extractor.extract({ files: [target.name] });
    for (const f of extracted.files) {
      if (f.extraction) {
        const u = f.extraction;
        return { name: baseName(target.name), bsp: u.buffer.slice(u.byteOffset, u.byteOffset + u.byteLength) as ArrayBuffer };
      }
    }
    throw new Error(`Could not extract ${target.name} from ${fileName}.`);
  }
  if (startsWith(bytes, SIG_ZIP)) {
    const files = unzipSync(bytes, { filter: (f) => /\.(bsp|bz2)$/i.test(f.name) });
    const list = Object.entries(files).map(([name, d]) => ({ name, size: d.byteLength, d }));
    const target = pickBsp(list, hint) ?? list.find((f) => /\.bsp\.bz2$/i.test(f.name));
    if (!target) throw new Error(`No .bsp file inside ${fileName}.`);
    const d = target.d;
    return extractMapArchive(d.buffer.slice(d.byteOffset, d.byteOffset + d.byteLength) as ArrayBuffer, target.name);
  }
  if (startsWith(bytes, SIG_7Z)) throw new Error('.7z archives are not supported — extract the .bsp and drop it instead.');
  throw new Error(`${fileName} is not a BSP map or a supported archive (.bsp, .bsp.bz2, .rar, .zip).`);
}

/** Cache → download → extract → cache. */
export async function fetchCatalogMap(
  entry: CatalogEntry,
  onProgress?: (p: LoadProgress) => void,
  signal?: AbortSignal,
): Promise<{ name: string; bsp: ArrayBuffer }> {
  const cached = await getCached(entry.name);
  if (cached) {
    onProgress?.({ phase: 'download', message: 'Loaded from cache', loaded: cached.byteLength, total: cached.byteLength });
    return { name: entry.name, bsp: cached };
  }
  onProgress?.({ phase: 'download', message: 'Contacting Google Drive…' });
  const archive = await download(entry.driveId, onProgress, signal);
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
  onProgress?.({ phase: 'extract', message: 'Extracting map…' });
  await new Promise((r) => setTimeout(r, 0));
  const { bsp } = await extractMapArchive(archive, `${entry.name}.${entry.archive ?? 'rar'}`);
  void putCached(entry.name, bsp);
  return { name: entry.name, bsp };
}
